import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { PageHeader } from "../../../components/ui";
import InkMixModal from "../../../components/InkMixModal";
import { listCardsPaged } from "../../../api/cards";
import { listBaseRawMaterialsPaged, receiveRawMaterial } from "../../../api/rawMaterials";
import { getMaterialVariantDimensions } from "../../../api/materials";
import { listDimensionOptions } from "../../../api/variantDimensions";
import ReferencePickerDialog from "../../../components/ReferencePickerDialog";

export default function InkIntakePage() {
  const [selectedMaterial, setSelectedMaterial] = useState(null);

  const [materialId, setMaterialId] = useState("");
  const [pantoneCode, setPantoneCode] = useState("");
  const [templateDimensions, setTemplateDimensions] = useState([]);
  const [colorDimension, setColorDimension] = useState(null);
  const [colorOptions, setColorOptions] = useState([]);
  const [colorOptionId, setColorOptionId] = useState("");
  const [colorLoadError, setColorLoadError] = useState("");

  const [supplierCardId, setSupplierCardId] = useState("");
  const [receivedByEmployeeCardId, setReceivedByEmployeeCardId] = useState("");
  const [supplierLabel, setSupplierLabel] = useState("");
  const [employeeLabel, setEmployeeLabel] = useState("");
  const [picker, setPicker] = useState(null);
  const [inboundDocNo, setInboundDocNo] = useState("");
  // Boyada otomatik lot numarası üretilmiyor; kullanıcı elle girer.
  const [lotNo, setLotNo] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [qty, setQty] = useState("");
  const [note, setNote] = useState("");
  const [unitCost, setUnitCost] = useState("");
  const [currency, setCurrency] = useState("TRY");
  const [priceUnit, setPriceUnit] = useState("");

  const [saving, setSaving] = useState(false);
  const [mixOpen, setMixOpen] = useState(false);
  const lastLoadReq = useRef(0);

  const loadInkMaterials = useCallback(({ page, search }) => listBaseRawMaterialsPaged("ink", { page, limit: 10, search }), []);
  const loadSuppliers = useCallback(({ page, search }) => listCardsPaged({ type: "supplier", page, limit: 10, search }), []);
  const loadEmployees = useCallback(({ page, search }) => listCardsPaged({ type: "employee", page, limit: 10, search }), []);
  const pickerConfig = picker === "material" ? {
    title: "Boya Stok Kartı Seç", searchPlaceholder: "Kod veya malzeme adı ile ara...", fetchPage: loadInkMaterials,
    getPrimary: (item) => `${item.internal_code || item.code || "-"} — ${item.name || "-"}`, getSecondary: (item) => item.unit || "Boya",
  } : picker === "supplier" ? {
    title: "Tedarikçi Seç", searchPlaceholder: "Tedarikçi adı ile ara...", fetchPage: loadSuppliers,
    getPrimary: (item) => item.name, getSecondary: (item) => item.code || "Tedarikçi kartı",
  } : picker === "employee" ? {
    title: "Teslim Alanı Seç", searchPlaceholder: "Personel adı ile ara...", fetchPage: loadEmployees,
    getPrimary: (item) => item.name, getSecondary: (item) => item.code || "Çalışan kartı",
  } : null;
  function selectReference(item) {
    if (picker === "material") { setMaterialId(String(item.id)); setSelectedMaterial(item); setColorOptionId(""); setPantoneCode(""); }
    if (picker === "supplier") { setSupplierCardId(String(item.id)); setSupplierLabel(item.name); }
    if (picker === "employee") { setReceivedByEmployeeCardId(String(item.id)); setEmployeeLabel(item.name); }
    setPicker(null);
  }


  useEffect(() => {
    let cancelled = false;
    async function loadTemplateAndColors() {
      try {
        const reqId = Date.now();
        lastLoadReq.current = reqId;
        setColorLoadError("");

        if (!materialId) {
          setTemplateDimensions([]);
          setColorDimension(null);
          setColorOptions([]);
          setColorOptionId("");
          return;
        }

        const tmpl = await getMaterialVariantDimensions(Number(materialId));
        if (cancelled) return;
        if (lastLoadReq.current !== reqId) return;

        const items = Array.isArray(tmpl) ? tmpl : [];
        setTemplateDimensions(items);

        const normalizeKey = (v) => String(v || "").trim().toLocaleLowerCase("tr-TR");
        const colorKeys = new Set(["renk", "color", "colour", "renk_adi", "renkadi", "color_name"]);
        const renk =
          items.find((d) => colorKeys.has(normalizeKey(d?.key))) ||
          items.find((d) => normalizeKey(d?.label).includes("renk")) ||
          items.find((d) => normalizeKey(d?.label).includes("color")) ||
          null;
        setColorDimension(renk);
        setColorOptionId("");

        const dimId = Number(renk?.dimension_id || renk?.id);
        if (!Number.isFinite(dimId)) {
          setColorOptions([]);
          return;
        }

        const opts = await listDimensionOptions(dimId);
        if (cancelled) return;
        if (lastLoadReq.current !== reqId) return;
        setColorOptions(Array.isArray(opts) ? opts : []);
      } catch (e) {
        console.error("INK TEMPLATE/COLOR LOAD ERROR", e);
        if (!cancelled) {
          setTemplateDimensions([]);
          setColorDimension(null);
          setColorOptions([]);
          setColorOptionId("");
          setColorLoadError(e?.response?.data?.error?.message || "Renk varyant seçenekleri yüklenemedi");
        }
      }
    }
    loadTemplateAndColors();
    return () => {
      cancelled = true;
    };
  }, [materialId]);

  async function onSave() {
    const q = Number(qty);

    if (!materialId) {
      alert("Boya malzeme kartı seçin");
      return;
    }
    if (!colorOptionId) {
      alert("Renk seçin");
      return;
    }
    if (!pantoneCode.trim()) {
      alert("Pantone code seçin");
      return;
    }
    if (!supplierCardId) {
      alert("Tedarikçi seçin");
      return;
    }
    if (!inboundDocNo.trim()) {
      alert("İrsaliye numarası girin");
      return;
    }
    if (!lotNo.trim()) {
      alert("Lot numarası girin");
      return;
    }
    if (!expiryDate) {
      alert("Son kullanma tarihi seçin");
      return;
    }
    if (!receivedByEmployeeCardId) {
      alert("Teslim alan seçin");
      return;
    }
    if (!Number.isFinite(q) || q <= 0) {
      alert("Miktar pozitif olmalı");
      return;
    }

    setSaving(true);
    try {
      const dimId =
        colorDimension?.dimension_id !== null && colorDimension?.dimension_id !== undefined
          ? Number(colorDimension.dimension_id)
          : colorDimension?.id !== null && colorDimension?.id !== undefined
            ? Number(colorDimension.id)
            : null;
      const selections =
        Number.isFinite(dimId) && Number(colorOptionId)
          ? [{ dimension_id: Number(dimId), option_id: Number(colorOptionId) }]
          : [];
      const payload = {
        material_id: Number(materialId),
        selections,
        color_option_id: Number(colorOptionId),
        pantone_code: pantoneCode.trim(),
        supplier_card_id: supplierCardId ? Number(supplierCardId) : null,
        inbound_doc_no: inboundDocNo.trim() ? inboundDocNo.trim() : null,
        lot_no: lotNo.trim(),
        expiry_date: expiryDate || null,
        received_by_employee_card_id: receivedByEmployeeCardId ? Number(receivedByEmployeeCardId) : null,
        qty: q,
        note: note.trim() ? note.trim() : null,
        unit_cost: unitCost !== "" ? Number(unitCost) : null,
        currency: currency || "TRY",
        price_unit: priceUnit || null,
      };
      const d = await receiveRawMaterial("ink", payload);
      alert(`Kaydedildi.\n\nLot: ${d.lot_no}\nBirim: ${d.unit || "-"}`);
      setQty("");
      setInboundDocNo("");
      setLotNo("");
      setExpiryDate("");
      setNote("");
      setUnitCost("");
      setCurrency("TRY");
      setPriceUnit("");
    } catch (err) {
      console.error("INK RECEIVE ERROR", err);
      alert(err?.response?.data?.error?.message || "Kaydetme başarısız");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Stack spacing={2.5} sx={{ maxWidth: 1100, mx: "auto", "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" } }}>
      <PageHeader
        title="Hammadde Giriş — Boya"
        subtitle="Boya stok kartını seçin, renk/pantone ve son kullanma tarihini girin."
        actions={
          <Stack direction="row" spacing={1} sx={{ width: { xs: "100%", md: "auto" } }}>
            <Button variant="outlined" onClick={() => setMixOpen(true)} fullWidth sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800, borderColor: "#818cf8", color: "#4338ca", "&:hover": { borderColor: "#4f46e5", bgcolor: "#eef2ff" } }}>
              Boya Karıştır
            </Button>
          </Stack>
        }
      />

      {mixOpen ? <InkMixModal onClose={() => setMixOpen(false)} onMixed={() => null} /> : null}

      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 4, bgcolor: "#f5f3ff", border: "1px solid #a5b4fc", boxShadow: "0 10px 30px rgba(79, 70, 229, 0.10)" }}>
            <Typography variant="h6" sx={{ fontWeight: 900, mb: 1.5, color: "#312e81" }}>
              Boya Bilgileri
            </Typography>

            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Boya Stok Kartı</Typography>
                <Button variant="outlined" fullWidth onClick={() => setPicker("material")} disabled={saving} sx={{ mt: 0.5, minHeight: 40, justifyContent: "flex-start", textTransform: "none", borderRadius: 2, color: materialId ? "#3730a3" : "text.secondary", fontWeight: materialId ? 700 : 400 }}>
                  {selectedMaterial ? `${selectedMaterial.internal_code || selectedMaterial.code || "-"} — ${selectedMaterial.name || "-"}` : "Boya stok kartı seçiniz"}
                </Button>
              </Box>

              <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                <FormControl
                  size="small"
                  disabled={!materialId || !colorDimension}
                  sx={{ minWidth: 260, flex: "1 1 260px" }}
                >
                      <InputLabel id="ink-color-label">Renk</InputLabel>
                      <Select
                        labelId="ink-color-label"
                        label="Renk"
                        value={colorOptionId}
                        onChange={(e) => setColorOptionId(String(e.target.value))}
                      >
                        <MenuItem value="">
                          <em>Seçiniz</em>
                        </MenuItem>
                        {colorOptions.map((o) => (
                          <MenuItem key={o.id} value={String(o.id)}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                <TextField
                  size="small"
                  label="Pantone Code"
                  value={pantoneCode}
                  onChange={(e) => setPantoneCode(e.target.value)}
                  placeholder="Örn: 123C"
                  sx={{ minWidth: 220, flex: "1 1 220px" }}
                />
              </Stack>

              {!materialId ? null : !colorDimension ? (
                <Alert severity="warning">
                  Bu boya kartında `renk` / `color` varyantı tanımlı değil. Malzeme kartı &gt; Varyant Şablonu’ndan ekleyin.
                </Alert>
              ) : null}

              {colorLoadError ? (
                <Alert severity="warning">{colorLoadError}</Alert>
              ) : null}
            </Stack>
          </Paper>

      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 4, bgcolor: "#f5f3ff", border: "1px solid #a5b4fc", boxShadow: "0 10px 30px rgba(79, 70, 229, 0.10)" }}>
            <Typography variant="h6" sx={{ fontWeight: 900, mb: 1.5, color: "#312e81" }}>
              Stok Girişi
            </Typography>

            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Tedarikçi</Typography>
                <Button variant="outlined" fullWidth onClick={() => setPicker("supplier")} disabled={saving} sx={{ mt: 0.5, minHeight: 40, justifyContent: "flex-start", textTransform: "none", borderRadius: 2, color: supplierCardId ? "#3730a3" : "text.secondary", fontWeight: supplierCardId ? 700 : 400 }}>
                  {supplierCardId ? supplierLabel : "Tedarikçi seçiniz"}
                </Button>
              </Box>

              <TextField size="small" label="İrsaliye Numarası" value={inboundDocNo} onChange={(e) => setInboundDocNo(e.target.value)} />

              <TextField
                size="small"
                label="Lot Numarası"
                value={lotNo}
                onChange={(e) => setLotNo(e.target.value)}
                inputProps={{ maxLength: 100 }}
              />

              <TextField
                size="small"
                type="date"
                label="Son Kullanma Tarihi"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />

              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Teslim Alan</Typography>
                <Button variant="outlined" fullWidth onClick={() => setPicker("employee")} disabled={saving} sx={{ mt: 0.5, minHeight: 40, justifyContent: "flex-start", textTransform: "none", borderRadius: 2, color: receivedByEmployeeCardId ? "#3730a3" : "text.secondary", fontWeight: receivedByEmployeeCardId ? 700 : 400 }}>
                  {receivedByEmployeeCardId ? employeeLabel : "Teslim alanı seçiniz"}
                </Button>
              </Box>

              <TextField size="small" label={`Miktar (${selectedMaterial?.unit || "—"})`} value={qty} onChange={(e) => setQty(e.target.value)} />
              <TextField size="small" label="Not (ops.)" value={note} onChange={(e) => setNote(e.target.value)} />

              <Stack direction="row" spacing={1}>
                <TextField size="small" label="Birim Fiyat" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} inputMode="decimal" sx={{ flex: 2 }} />
                <FormControl size="small" sx={{ flex: 1, minWidth: 90 }}>
                  <InputLabel>Fiyatın Birimi</InputLabel>
                  <Select value={priceUnit} label="Fiyatın Birimi" onChange={(e) => setPriceUnit(e.target.value)}>
                    <MenuItem value=""><em>—</em></MenuItem>
                    <MenuItem value="kg">kg</MenuItem>
                    <MenuItem value="adet">adet</MenuItem>
                    <MenuItem value="m">m</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ flex: 1, minWidth: 90 }}>
                  <InputLabel>Döviz</InputLabel>
                  <Select value={currency} label="Döviz" onChange={(e) => setCurrency(e.target.value)}>
                    <MenuItem value="TRY">TRY</MenuItem>
                    <MenuItem value="USD">USD</MenuItem>
                    <MenuItem value="EUR">EUR</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              <Button variant="contained" fullWidth disabled={saving} onClick={onSave} sx={{ mt: 0.5, py: 1.2, borderRadius: 2.25, textTransform: "none", fontWeight: 800, bgcolor: "#4f46e5", "&:hover": { bgcolor: "#3730a3" } }}>
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </Button>
            </Stack>
          </Paper>
      {pickerConfig ? <ReferencePickerDialog open={Boolean(picker)} {...pickerConfig} onClose={() => setPicker(null)} onSelect={selectReference} /> : null}
    </Stack>
  );
}

